import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { EmcNetworkScanButton } from '../../../components/Dialogs/components/NetworkScanButton'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Emc, Storage } from 'src/Libs/flow/installation/forms'
import { logError } from 'src/Libs/flow/utils'
import { getIsEmcOnline } from 'src/Libs/flow/installation/utils'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useFetchScanEmcFarm } from '../../hooks/use-fetch-scan-emc-farm'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useDialogActions } from '../../../components/Dialogs/hooks'
import { QrScanButton, QrPairingEditor } from 'src/Libs/installations'

/** Allow only alphanumeric characters and the - symbol. Also allows an empty string. */
const PAIRING_ID_REGEX = /^([a-z0-9]|-)*$/i

//#region Dialog Component
export interface StoragePairingDialogProps {
  installationId: number | null
  emc: Emc | null
  storage: Storage
  submitPairingId: (id: string) => void
  onClose?: () => void
}

// TODO: test with real device
export const StoragePairingDialog: React.FC<StoragePairingDialogProps> = ({ installationId, emc, storage, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const [pairingId, setPairingId] = useState(storage.pairingId)

  const pairingIdContainsSpecialCharacters = !PAIRING_ID_REGEX.test(pairingId)

  const error = pairingIdContainsSpecialCharacters ? t('installation.productEditor.pairingEditor.no-special-characters') : undefined

  const isSame = storage.pairingId === pairingId

  const handleSubmit = (): void => {
    /** Dashes are allow during typing, but must be removed on submition. */
    submitPairingId(pairingId.replaceAll('-', ''))

    onClose?.()
  }

  const [startQrScanner, setStartQrScanner] = useState(false)
  const [startNetworkScanner, setStartNetworkScanner] = useState(false)

  const isEmcPaired = !!emc?.isPaired
  const isEmcOnline = getIsEmcOnline(emc)

  const handleOnClose = (): void => {
    onClose?.()
  }

  const primaryName = storage.isNew ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')
  const dialogActions = useDialogActions({
    primary: {
      name: primaryName,
      onClick: handleSubmit,
      disabled: !!error || isSame,
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth="md" onClose={handleOnClose} actions={dialogActions} title={t('installation.productEditor.pairingEditor.title.storage')}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, my: 2 }}>
        <TextField
          name="Emc pairing id"
          label={t('productEditor.pairingEditor.pairingID.storage')}
          fullWidth
          value={pairingId}
          onChange={e => setPairingId(e.target.value)}
          inputProps={{ maxLength: 50 }}
          error={!!error}
          helperText={error}
        />

        <EmcNetworkScanButton
          emc={emc}
          installationId={installationId}
          onClick={() => setStartNetworkScanner(!startNetworkScanner)}
          disabled={!isEmcOnline}
        />
        <QrScanButton onClick={() => setStartQrScanner(!startQrScanner)} disabled={!isEmcPaired} />
      </Box>

      <QrPairingEditor startQrScanner={startQrScanner} setStartQrScanner={setStartQrScanner} setPairingId={setPairingId} />

      {startNetworkScanner && (
        <StoragePairingEditor
          installationId={installationId}
          emcId={emc?.deviceId.toString()}
          setPairingId={setPairingId}
          currentPairingId={pairingId}
        />
      )}
    </Dialog>
  )
}
//#endregion Dialog Component

//#region Storage Pairing Editor

interface StoragePairingEditorProps {
  installationId: number | null
  emcId: string | undefined
  setPairingId: (pairingId: string) => void
  currentPairingId: string | undefined
}

const StoragePairingEditor: React.FC<StoragePairingEditorProps> = ({ installationId, emcId, setPairingId, currentPairingId }) => {
  const { t } = useCustomTranslation()

  const { data, isPending, error } = useFetchScanEmcFarm(installationId, emcId)
  if (error) {
    return <Typography color="error">{error.message}</Typography>
  }

  if (isPending || !data) {
    return (
      <Box textAlign={'center'}>
        <CircularProgress />
      </Box>
    )
  }

  if (typeof data !== 'object') {
    logError('Storage network scan data is not of type object.')
    return null
  }

  if (!data.length) {
    return <Alert severity="error" text={t('installation.productEditor.scanNetwork.noResults')} />
  }

  return (
    <Box maxHeight={500}>
      {data.map(storage => {
        // TODO: Test in QA for validity and completenes of this parsing.
        // Storage pairing id has another possible prefix XCM.
        const pairingId = storage.prefix === 'XEM' ? storage.serialno?.toString() : storage.shortUid

        const isCurrentStorage = `${pairingId}` === `${currentPairingId}`

        return (
          <NetworkScanResultStorage
            key={storage.serialno}
            pairingId={pairingId}
            handleSelect={() => setPairingId(pairingId)}
            isCurrentStorage={isCurrentStorage}
          />
        )
      })}
    </Box>
  )
}
//#endregion

//#region Network Scan Result

interface NetworkScanResultStorageProps {
  pairingId: string
  handleSelect: () => void
  isCurrentStorage: boolean
}

const NetworkScanResultStorage: React.FC<NetworkScanResultStorageProps> = ({ pairingId, handleSelect, isCurrentStorage }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        py: 3,
        borderBottom: theme => `1px solid ${theme.palette.grey[500]}`,
        display: 'flex',
        gap: 1,
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'initial', sm: 'center' },
        justifyContent: { xs: 'initial', sm: 'space-between' },
      }}
    >
      <Box sx={{ lineHeight: 1.5 }}>
        <Typography sx={{ minWidth: 100 }}>{t('installation.productEditor.scanNetwork.shortId')}</Typography>
        <Typography sx={{ fontWeight: 'bold' }}>{pairingId}</Typography>
      </Box>

      <Button size="large" color={'secondary'} variant={'outlined'} onClick={handleSelect} disabled={isCurrentStorage}>
        {isCurrentStorage ? t('installation.productEditor.scanNetwork.selected') : t('installation.productEditor.scanNetwork.select')}
      </Button>
    </Box>
  )
}
